import * as React from "react"
import { Search, X } from "lucide-react"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

interface SearchInputProps
  extends Omit<React.ComponentProps<typeof Input>, "type"> {
  /** Accessible label, rendered visually hidden. Falls back to `placeholder`. */
  label?: string
  /** When provided, a clear button appears while the field holds a value. */
  onClear?: () => void
  /** Accessible label for the clear button. Falls back to the field label. */
  clearLabel?: string
  /** Class for the relative wrapper — set width here, not on the input. */
  containerClassName?: string
}

/**
 * The operational search field: the kit `Input type="search"` with a leading
 * `Search` glyph and an optional clear affordance. This is the one search
 * vocabulary for admin and staff (Operate) surfaces; the diner domain keeps
 * its rounded `bg-card` variant. See DESIGN.md "Inputs / Fields".
 */
function SearchInput({
  label,
  onClear,
  clearLabel,
  className,
  containerClassName,
  id,
  placeholder,
  value,
  ...props
}: SearchInputProps) {
  const generatedId = React.useId()
  const inputId = id ?? generatedId
  const accessibleLabel =
    label ?? (typeof placeholder === "string" ? placeholder : undefined)
  const showClear = Boolean(onClear) && value != null && value !== ""

  return (
    <div className={cn("relative", containerClassName)}>
      {accessibleLabel && (
        <Label htmlFor={inputId} className="sr-only">
          {accessibleLabel}
        </Label>
      )}
      <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
      <Input
        id={inputId}
        type="search"
        value={value}
        placeholder={placeholder}
        className={cn(
          "pl-9 [&::-webkit-search-cancel-button]:hidden",
          showClear && "pr-9",
          className
        )}
        {...props}
      />
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label={clearLabel ?? accessibleLabel}
          className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring/50 absolute top-1/2 right-1.5 flex size-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  )
}

export { SearchInput }
